import type { TriangleVertices } from '@data/geometry/Functions';
import FVector from '@data/geometry/FVector';

// Axis-aligned bounds are used as a cheap lower-bound for point-to-triangle distance.
// If the point is already farther from the box than the current best hit, the whole subtree can be skipped.
type BoundingBox = {
    minX: number;
    minY: number;
    minZ: number;
    maxX: number;
    maxY: number;
    maxZ: number;
};

// Each triangle is stored together with its bounds and centroid so tree construction
// does not need to recompute those values at every recursion step.
type IndexedTriangle = {
    bounds: BoundingBox;
    centroidX: number;
    centroidY: number;
    centroidZ: number;
    aX: number;
    aY: number;
    aZ: number;
    bX: number;
    bY: number;
    bZ: number;
    cX: number;
    cY: number;
    cZ: number;
    abX: number;
    abY: number;
    abZ: number;
    acX: number;
    acY: number;
    acZ: number;
    bcX: number;
    bcY: number;
    bcZ: number;
    caX: number;
    caY: number;
    caZ: number;
    abLengthSq: number;
    bcLengthSq: number;
    caLengthSq: number;
    normalX: number;
    normalY: number;
    normalZ: number;
    normalMagSq: number;
    invNormalMagSq: number;
    planeConstant: number;
    baryDot00: number;
    baryDot01: number;
    baryDot11: number;
    baryInvDenom: number;
};

export type TriangleSpatialNode = {
    bounds: BoundingBox;
    triangles?: IndexedTriangle[];
    left?: TriangleSpatialNode;
    right?: TriangleSpatialNode;
};

// Small leaves keep per-node scans cheap while avoiding an overly deep tree.
const TRIANGLE_BVH_LEAF_SIZE = 16;
const BARYCENTRIC_EPSILON = 1e-9;

export function indexTriangles(triangles: TriangleVertices[]): IndexedTriangle[] {
    // Precompute all values needed by the tree once, before any recursive build work starts.
    return triangles.map((triangle) => {
        const bounds = getTriangleBounds(triangle);
        const aX = triangle.A.x;
        const aY = triangle.A.y;
        const aZ = triangle.A.z;
        const bX = triangle.B.x;
        const bY = triangle.B.y;
        const bZ = triangle.B.z;
        const cX = triangle.C.x;
        const cY = triangle.C.y;
        const cZ = triangle.C.z;

        const abX = bX - aX;
        const abY = bY - aY;
        const abZ = bZ - aZ;
        const acX = cX - aX;
        const acY = cY - aY;
        const acZ = cZ - aZ;
        const bcX = cX - bX;
        const bcY = cY - bY;
        const bcZ = cZ - bZ;
        const caX = aX - cX;
        const caY = aY - cY;
        const caZ = aZ - cZ;

        const abLengthSq = abX * abX + abY * abY + abZ * abZ;
        const bcLengthSq = bcX * bcX + bcY * bcY + bcZ * bcZ;
        const caLengthSq = caX * caX + caY * caY + caZ * caZ;

        const normalX = abY * acZ - acY * abZ;
        const normalY = abZ * acX - acZ * abX;
        const normalZ = abX * acY - acX * abY;
        const normalMagSq = normalX * normalX + normalY * normalY + normalZ * normalZ;

        const baryDot00 = acX * acX + acY * acY + acZ * acZ;
        const baryDot01 = acX * abX + acY * abY + acZ * abZ;
        const baryDot11 = abX * abX + abY * abY + abZ * abZ;
        const baryDenom = baryDot00 * baryDot11 - baryDot01 * baryDot01;

        return {
            bounds,
            centroidX: (bounds.minX + bounds.maxX) / 2,
            centroidY: (bounds.minY + bounds.maxY) / 2,
            centroidZ: (bounds.minZ + bounds.maxZ) / 2,
            aX,
            aY,
            aZ,
            bX,
            bY,
            bZ,
            cX,
            cY,
            cZ,
            abX,
            abY,
            abZ,
            acX,
            acY,
            acZ,
            bcX,
            bcY,
            bcZ,
            caX,
            caY,
            caZ,
            abLengthSq,
            bcLengthSq,
            caLengthSq,
            normalX,
            normalY,
            normalZ,
            normalMagSq,
            invNormalMagSq: normalMagSq === 0 ? 0 : 1 / normalMagSq,
            planeConstant: -(normalX * aX + normalY * aY + normalZ * aZ),
            baryDot00,
            baryDot01,
            baryDot11,
            baryInvDenom: baryDenom === 0 ? 0 : 1 / baryDenom,
        };
    });
}

export function buildTriangleSpatialTree(triangles: IndexedTriangle[]): TriangleSpatialNode | undefined {
    if (triangles.length === 0) {
        return undefined;
    }

    const bounds = mergeBounds(triangles);
    if (triangles.length <= TRIANGLE_BVH_LEAF_SIZE) {
        // Leaves own the exact triangles. Internal nodes only carry aggregate bounds plus child references.
        return { bounds, triangles };
    }

    // Split along the widest axis so sibling bounds overlap less on average.
    const axis = getLongestAxis(bounds);
    const sortedTriangles = [...triangles].sort((left, right) => getTriangleCentroid(left, axis) - getTriangleCentroid(right, axis));
    const middleIndex = Math.floor(sortedTriangles.length / 2);
    const leftTriangles = sortedTriangles.slice(0, middleIndex);
    const rightTriangles = sortedTriangles.slice(middleIndex);

    if (leftTriangles.length === 0 || rightTriangles.length === 0) {
        // Degenerate split fallback: keep everything in one leaf rather than recursing forever on identical centroids.
        return { bounds, triangles: sortedTriangles };
    }

    return {
        bounds,
        left: buildTriangleSpatialTree(leftTriangles),
        right: buildTriangleSpatialTree(rightTriangles),
    };
}

export function computeClosestPerpendicularDistance(position: FVector, tree?: TriangleSpatialNode, maxDistance = Infinity): number {
    // Used for roof/wall distances where only orthogonal projection onto the triangle face counts.
    return computeClosestDistanceWithTree(position, tree, pointTrianglePerpendicularDistanceSq, maxDistance);
}

export function computeClosestDistance(position: FVector, tree?: TriangleSpatialNode, maxDistance = Infinity): number {
    // Used for the overall distance where triangle edges are also valid closest features.
    return computeClosestDistanceWithTree(position, tree, pointTriangleDistanceSq, maxDistance);
}

function computeClosestDistanceWithTree(
    position: FVector,
    tree: TriangleSpatialNode | undefined,
    getDistanceSq: (pointX: number, pointY: number, pointZ: number, triangle: IndexedTriangle) => number,
    maxDistance: number
): number {
    if (!tree) {
        return Infinity;
    }

    const pointX = position.x;
    const pointY = position.y;
    const pointZ = position.z;
    let closestDistanceSq = maxDistance === Infinity ? Infinity : maxDistance * maxDistance;

    // Visiting the nearer child first usually tightens the best distance quickly enough
    // that the farther subtree can be pruned without maintaining a fully sorted queue.
    visit(tree, distanceToBoundingBoxSq(pointX, pointY, pointZ, tree.bounds));

    return closestDistanceSq === Infinity ? Infinity : Math.sqrt(closestDistanceSq);

    function visit(node: TriangleSpatialNode, nodeDistanceSq: number): boolean {
        if (nodeDistanceSq >= closestDistanceSq) {
            return false;
        }

        if (node.triangles) {
            for (const indexedTriangle of node.triangles) {
                const distanceSq = getDistanceSq(pointX, pointY, pointZ, indexedTriangle);
                if (distanceSq < closestDistanceSq) {
                    closestDistanceSq = distanceSq;
                    if (closestDistanceSq === 0) {
                        return true;
                    }
                }
            }
            return false;
        }

        const leftNode = node.left;
        const rightNode = node.right;

        if (leftNode && rightNode) {
            const leftDistanceSq = distanceToBoundingBoxSq(pointX, pointY, pointZ, leftNode.bounds);
            const rightDistanceSq = distanceToBoundingBoxSq(pointX, pointY, pointZ, rightNode.bounds);

            if (leftDistanceSq <= rightDistanceSq) {
                if (visit(leftNode, leftDistanceSq)) {
                    return true;
                }
                if (rightDistanceSq < closestDistanceSq) {
                    return visit(rightNode, rightDistanceSq);
                }
                return false;
            }

            if (visit(rightNode, rightDistanceSq)) {
                return true;
            }
            if (leftDistanceSq < closestDistanceSq) {
                return visit(leftNode, leftDistanceSq);
            }
            return false;
        }

        if (leftNode) {
            return visit(leftNode, distanceToBoundingBoxSq(pointX, pointY, pointZ, leftNode.bounds));
        }

        if (rightNode) {
            return visit(rightNode, distanceToBoundingBoxSq(pointX, pointY, pointZ, rightNode.bounds));
        }

        return false;
    }
}

function getTriangleBounds(triangle: TriangleVertices): BoundingBox {
    // The bounds fully enclose the triangle and are used only for pruning, never as an approximate final answer.
    return {
        minX: Math.min(triangle.A.x, triangle.B.x, triangle.C.x),
        minY: Math.min(triangle.A.y, triangle.B.y, triangle.C.y),
        minZ: Math.min(triangle.A.z, triangle.B.z, triangle.C.z),
        maxX: Math.max(triangle.A.x, triangle.B.x, triangle.C.x),
        maxY: Math.max(triangle.A.y, triangle.B.y, triangle.C.y),
        maxZ: Math.max(triangle.A.z, triangle.B.z, triangle.C.z),
    };
}

function mergeBounds(triangles: IndexedTriangle[]): BoundingBox {
    // Internal node bounds are the union of all descendant triangle boxes.
    const [firstTriangle, ...restTriangles] = triangles;
    const bounds = { ...firstTriangle.bounds };

    for (const triangle of restTriangles) {
        bounds.minX = Math.min(bounds.minX, triangle.bounds.minX);
        bounds.minY = Math.min(bounds.minY, triangle.bounds.minY);
        bounds.minZ = Math.min(bounds.minZ, triangle.bounds.minZ);
        bounds.maxX = Math.max(bounds.maxX, triangle.bounds.maxX);
        bounds.maxY = Math.max(bounds.maxY, triangle.bounds.maxY);
        bounds.maxZ = Math.max(bounds.maxZ, triangle.bounds.maxZ);
    }

    return bounds;
}

function getLongestAxis(bounds: BoundingBox): 'x' | 'y' | 'z' {
    // This simple heuristic usually produces better partitions than splitting on a fixed axis.
    const xLength = bounds.maxX - bounds.minX;
    const yLength = bounds.maxY - bounds.minY;
    const zLength = bounds.maxZ - bounds.minZ;

    if (xLength >= yLength && xLength >= zLength) {
        return 'x';
    }
    if (yLength >= zLength) {
        return 'y';
    }
    return 'z';
}

function getTriangleCentroid(triangle: IndexedTriangle, axis: 'x' | 'y' | 'z'): number {
    // The centroid is only used during construction to sort triangles before the median split.
    switch (axis) {
        case 'x':
            return triangle.centroidX;
        case 'y':
            return triangle.centroidY;
        default:
            return triangle.centroidZ;
    }
}

function pointTrianglePerpendicularDistanceSq(pointX: number, pointY: number, pointZ: number, triangle: IndexedTriangle): number {
    if (triangle.normalMagSq === 0) {
        return Infinity;
    }

    const signedPlaneDistance = triangle.normalX * pointX + triangle.normalY * pointY + triangle.normalZ * pointZ + triangle.planeConstant;
    const projectionScale = -signedPlaneDistance * triangle.invNormalMagSq;
    const projectedX = pointX + triangle.normalX * projectionScale;
    const projectedY = pointY + triangle.normalY * projectionScale;
    const projectedZ = pointZ + triangle.normalZ * projectionScale;

    if (!pointInsideTriangle(projectedX, projectedY, projectedZ, triangle)) {
        return Infinity;
    }

    return signedPlaneDistance * signedPlaneDistance * triangle.invNormalMagSq;
}

function pointTriangleDistanceSq(pointX: number, pointY: number, pointZ: number, triangle: IndexedTriangle): number {
    if (triangle.normalMagSq !== 0) {
        const signedPlaneDistance =
            triangle.normalX * pointX + triangle.normalY * pointY + triangle.normalZ * pointZ + triangle.planeConstant;
        const projectionScale = -signedPlaneDistance * triangle.invNormalMagSq;
        const projectedX = pointX + triangle.normalX * projectionScale;
        const projectedY = pointY + triangle.normalY * projectionScale;
        const projectedZ = pointZ + triangle.normalZ * projectionScale;

        if (pointInsideTriangle(projectedX, projectedY, projectedZ, triangle)) {
            return signedPlaneDistance * signedPlaneDistance * triangle.invNormalMagSq;
        }
    }

    return Math.min(
        pointToSegmentDistanceSq(
            pointX,
            pointY,
            pointZ,
            triangle.aX,
            triangle.aY,
            triangle.aZ,
            triangle.abX,
            triangle.abY,
            triangle.abZ,
            triangle.abLengthSq
        ),
        pointToSegmentDistanceSq(
            pointX,
            pointY,
            pointZ,
            triangle.bX,
            triangle.bY,
            triangle.bZ,
            triangle.bcX,
            triangle.bcY,
            triangle.bcZ,
            triangle.bcLengthSq
        ),
        pointToSegmentDistanceSq(
            pointX,
            pointY,
            pointZ,
            triangle.cX,
            triangle.cY,
            triangle.cZ,
            triangle.caX,
            triangle.caY,
            triangle.caZ,
            triangle.caLengthSq
        )
    );
}

function pointInsideTriangle(pointX: number, pointY: number, pointZ: number, triangle: IndexedTriangle): boolean {
    if (triangle.baryInvDenom === 0) {
        return false;
    }

    const v2X = pointX - triangle.aX;
    const v2Y = pointY - triangle.aY;
    const v2Z = pointZ - triangle.aZ;

    const dot02 = triangle.acX * v2X + triangle.acY * v2Y + triangle.acZ * v2Z;
    const dot12 = triangle.abX * v2X + triangle.abY * v2Y + triangle.abZ * v2Z;

    const u = (triangle.baryDot11 * dot02 - triangle.baryDot01 * dot12) * triangle.baryInvDenom;
    const v = (triangle.baryDot00 * dot12 - triangle.baryDot01 * dot02) * triangle.baryInvDenom;

    return u >= -BARYCENTRIC_EPSILON && v >= -BARYCENTRIC_EPSILON && u + v <= 1 + BARYCENTRIC_EPSILON;
}

function pointToSegmentDistanceSq(
    pointX: number,
    pointY: number,
    pointZ: number,
    startX: number,
    startY: number,
    startZ: number,
    edgeX: number,
    edgeY: number,
    edgeZ: number,
    edgeLengthSq: number
): number {
    const apX = pointX - startX;
    const apY = pointY - startY;
    const apZ = pointZ - startZ;

    if (edgeLengthSq === 0) {
        return apX * apX + apY * apY + apZ * apZ;
    }

    const projectionFactor = clamp((apX * edgeX + apY * edgeY + apZ * edgeZ) / edgeLengthSq, 0, 1);
    const closestX = startX + edgeX * projectionFactor;
    const closestY = startY + edgeY * projectionFactor;
    const closestZ = startZ + edgeZ * projectionFactor;
    const dx = pointX - closestX;
    const dy = pointY - closestY;
    const dz = pointZ - closestZ;

    return dx * dx + dy * dy + dz * dz;
}

function distanceToBoundingBoxSq(pointX: number, pointY: number, pointZ: number, bounds: BoundingBox): number {
    // Standard point-to-AABB distance: zero when the point lies inside the box, squared otherwise.
    // Keeping comparisons in squared space avoids repeated square roots in the traversal hot path.
    const dx = Math.max(bounds.minX - pointX, 0, pointX - bounds.maxX);
    const dy = Math.max(bounds.minY - pointY, 0, pointY - bounds.maxY);
    const dz = Math.max(bounds.minZ - pointZ, 0, pointZ - bounds.maxZ);
    return dx * dx + dy * dy + dz * dz;
}

function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}
